import { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { mt, MBtn, MCard, SEV_COLOR, fmtT } from "./shared";

const ACK_ACTIONS = [
  ["acknowledged", "ACKNOWLEDGE", mt.green], ["responding", "RESPONDING", mt.blue],
  ["unable_to_respond", "UNABLE", mt.amber], ["request_info", "MORE INFO", mt.cyan],
  ["forward_supervisor", "FWD SUPERVISOR", mt.purple],
];

function AuditModal({ alert, onClose }) {
  if (!alert) return null;
  return (
    <div data-testid="mobile-alert-audit" className="fixed inset-0 z-[860] flex items-end" style={{ background: "rgba(0,0,0,0.75)" }}>
      <div className="w-full rounded-t-2xl p-4 pb-8 max-h-[80vh] overflow-y-auto" style={{ background: mt.bg, border: `1px solid ${mt.border}` }}>
        <div className="flex items-center justify-between mb-2">
          <span className="font-head font-bold text-sm" style={{ color: mt.text }}>ALERT AUDIT TRAIL</span>
          <MBtn small outline color={mt.dim} onClick={onClose} testid="mobile-alert-audit-close">CLOSE</MBtn>
        </div>
        <div className="text-xs font-mono mb-2" style={{ color: mt.dim }}>
          {alert.title} · created by {alert.created_by} · {fmtT(alert.ts)}
        </div>
        <div className="flex flex-col gap-1.5">
          {(alert.events || []).map((e, i) => (
            <div key={i} className="text-[11px] font-mono py-1.5 px-2 rounded" style={{ background: mt.surface, color: mt.text }}>
              <span style={{ color: mt.cyan }}>{fmtT(e.ts)}</span> — <b>{e.action.toUpperCase()}</b> by {e.user}
              {e.detail ? ` · ${e.detail}` : ""}
            </div>
          ))}
          {(alert.escalations || []).map((e, i) => (
            <div key={`esc${i}`} className="text-[11px] font-mono py-1.5 px-2 rounded" style={{ background: "#3b0d0d", color: "#FCA5A5" }}>
              {fmtT(e.ts)} — ESCALATION LEVEL {e.level} → {(e.notified || []).join(", ")}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function MobileAlerts({ alerts, user, onRefresh }) {
  const [audit, setAudit] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [resolution, setResolution] = useState("");
  const isSup = ["supervisor", "watch_commander", "records_supervisor", "admin",
    "police_dispatcher", "fire_dispatcher", "ems_dispatcher", "call_taker"].includes(user.role);

  const ack = async (a, action) => {
    try {
      await client.post(`/mobile/alerts/${a.id}/ack`, { action });
      toast.success(`Alert ${action.replace(/_/g, " ")}`);
      onRefresh();
    } catch { toast.error("Acknowledgment failed"); }
  };
  const openAudit = async (a) => {
    try {
      await client.post(`/mobile/alerts/${a.id}/open`, {});
      const r = await client.get(`/mobile/alerts/${a.id}/audit`);
      setAudit(r.data);
    } catch { toast.error("Could not load audit trail"); }
  };
  const resolve = async () => {
    try {
      await client.post(`/mobile/alerts/${resolving.id}/resolve`, { resolution: resolution.trim() });
      toast.success("Alert resolved");
      setResolving(null); setResolution(""); onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed"); }
  };

  const myAck = (a) => (a.acks || []).find((x) => x.user === user.name);

  return (
    <div data-testid="mobile-alerts" className="flex flex-col gap-3 p-3 pb-28">
      <span className="font-head font-bold text-sm" style={{ color: mt.text }}>ALERT CENTER ({alerts.length})</span>
      {alerts.length === 0 && <MCard><span className="text-sm font-mono" style={{ color: mt.dim }}>No alerts.</span></MCard>}
      {alerts.map((a) => {
        const sev = SEV_COLOR[a.severity] || mt.cyan;
        const acked = myAck(a);
        const needsAck = a.require_ack && !a.resolved && !acked;
        return (
          <MCard key={a.id} testid={`mobile-alert-${a.id}`}
            style={{ border: `1.5px solid ${needsAck ? sev : mt.border}` }}>
            <div className="flex items-center justify-between mb-1">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-head font-bold" style={{ background: sev, color: "#fff" }}>
                {(a.severity || "").toUpperCase()}
              </span>
              <span className="text-[10px] font-mono" style={{ color: mt.dim }}>{fmtT(a.ts)}</span>
            </div>
            <div className="font-head font-bold text-sm mb-0.5" style={{ color: mt.text }}>{a.title}</div>
            {a.message && <div className="text-xs font-mono mb-1" style={{ color: mt.dim }}>{a.message}</div>}
            <div className="text-[10px] font-mono" style={{ color: mt.dim }}>
              {a.category} · from {a.created_by}
              {a.incident_cc ? ` · ${a.incident_cc}` : ""}{a.unit ? ` · unit ${a.unit}` : ""}
              {(a.escalations || []).length > 0 && <span style={{ color: "#FCA5A5" }}> · ESCALATED ×{a.escalations.length}</span>}
              {a.resolved && <span style={{ color: mt.green }}> · RESOLVED by {a.resolved.by}</span>}
              {acked && <span style={{ color: mt.green }}> · you: {acked.action.replace(/_/g, " ")}</span>}
            </div>
            {needsAck && (
              <div className="grid grid-cols-3 gap-1.5 mt-2">
                {ACK_ACTIONS.map(([k, label, color]) => (
                  <MBtn key={k} small color={color} outline={k !== "acknowledged"}
                    onClick={() => ack(a, k)} testid={`mobile-alert-ack-${k}-${a.id}`}>{label}</MBtn>
                ))}
              </div>
            )}
            <div className="flex gap-1.5 mt-2">
              <MBtn small outline color={mt.cyan} onClick={() => openAudit(a)} testid={`mobile-alert-audit-btn-${a.id}`}>AUDIT</MBtn>
              {isSup && !a.resolved && (
                <MBtn small outline color={mt.green} onClick={() => setResolving(a)} testid={`mobile-alert-resolve-btn-${a.id}`}>RESOLVE</MBtn>
              )}
            </div>
          </MCard>
        );
      })}
      <AuditModal alert={audit} onClose={() => setAudit(null)} />
      {resolving && (
        <div className="fixed inset-0 z-[860] flex items-end" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="w-full rounded-t-2xl p-4 pb-8" style={{ background: mt.bg, border: `1px solid ${mt.border}` }}>
            <div className="font-head font-bold text-sm mb-2" style={{ color: mt.text }}>RESOLVE ALERT — {resolving.title}</div>
            <input data-testid="mobile-alert-resolution" value={resolution} onChange={(e) => setResolution(e.target.value)}
              placeholder="Resolution (required)" className="w-full mb-3 px-3 py-3 rounded-lg font-mono text-sm"
              style={{ background: mt.surface, border: `1px solid ${mt.border}`, color: mt.text }} />
            <div className="grid grid-cols-2 gap-2">
              <MBtn full color={mt.green} disabled={!resolution.trim()} onClick={resolve} testid="mobile-alert-resolve-confirm">RESOLVE</MBtn>
              <MBtn full outline color={mt.dim} onClick={() => setResolving(null)} testid="mobile-alert-resolve-cancel">CANCEL</MBtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
